// Editorial review queue for auto-classified document_excerpt entries.
//
// Route: /admin/knowledge/review
//
// Lists all document_excerpt entries with editorial_status='pending_review'
// (in Mongo: type='document_excerpt' + status='draft'). Sorted by
// confidence ascending — the editor reviews lowest-confidence chunks
// first because that's where their judgment adds the most value.
//
// Per-row actions:
//   - Approve            → editorial_status='approved', status='published'
//   - Re-tag (domain ▼) → swap domain, leaves editorial_status pending
//   - Edit excerpt text  → opens inline editor, PATCH /kb-entries/{id}
//                          with new text_chunk (server stamps edited_at/by;
//                          original_text_chunk preserved for citations)
//   - Reject (reason)    → editorial_status='rejected', status='archived'
//
// V1 SCOPE: list is fetched as summaries via /kb-entries?type=document_excerpt&status=draft,
// then full bodies fetched in parallel (one GET per summary). Pragmatic
// for the ~95-115 entries each book ingests; if the queue grows past a
// few hundred we add a dedicated review-queue endpoint.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spinner } from '@gc-coach/design-system';
import type {
  Domain,
  DocumentExcerptBody,
  KBEntry,
} from '@gc-coach/shared';
import { ALL_DOMAINS } from '@gc-coach/shared';
import { AdminShell, PageHeader } from '../shell/index.js';
import { useI18n } from '../i18n/index.js';
import {
  getKBEntry,
  listKBEntries,
  patchKBEntry,
  reviewExcerpt,
} from '../api/endpoints.js';
import styles from './ExcerptReview.module.css';

type SortBy = 'confidence_asc' | 'confidence_desc' | 'updated_desc';

export function ExcerptReviewPage() {
  const { t, tt } = useI18n();

  const [entries, setEntries] = useState<KBEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortBy>('confidence_asc');
  const [domainFilter, setDomainFilter] = useState<Domain | ''>('');

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    const listResult = await listKBEntries({
      type: 'document_excerpt',
      status: 'draft',
    });
    if (!listResult.ok) {
      setLoadError(listResult.message);
      setIsLoading(false);
      return;
    }
    const summaries = listResult.data.entries;

    // Fetch full bodies in parallel.
    const settled = await Promise.allSettled(
      summaries.map((s) => getKBEntry(s.id)),
    );
    const fulls: KBEntry[] = [];
    for (const r of settled) {
      if (r.status === 'fulfilled' && r.value.ok) {
        fulls.push(r.value.data.entry);
      }
    }
    setEntries(fulls);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Filter + sort.
  const visible = useMemo(() => {
    let v = entries.filter((e) => e.type === 'document_excerpt');
    // Only show pending_review entries (some may have been approved/rejected since fetch).
    v = v.filter((e) => (e.body as DocumentExcerptBody).editorial_status === 'pending_review');
    if (domainFilter) v = v.filter((e) => e.domain === domainFilter);
    v = [...v].sort((a, b) => {
      const ca = (a.body as DocumentExcerptBody).confidence ?? 0;
      const cb = (b.body as DocumentExcerptBody).confidence ?? 0;
      if (sortBy === 'confidence_asc') return ca - cb;
      if (sortBy === 'confidence_desc') return cb - ca;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
    return v;
  }, [entries, sortBy, domainFilter]);

  return (
    <AdminShell
      activeKey="review"
      breadcrumb={[t('shell.sectionKnowledge'), t('shell.navReview')]}
      state={{ kind: 'normal' }}
    >
      <PageHeader
        title={t('review.pageTitle')}
        sub={tt('review.pageSubTemplate', { count: visible.length })}
      />

      {loadError && (
        <Alert severity="danger" onDismiss={() => setLoadError(null)}>
          {loadError}
        </Alert>
      )}

      {/* Filter / sort row */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarGroup}>
          <label className={styles.toolbarLabel}>{t('review.sortLabel')}</label>
          <select
            className={styles.select}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortBy)}
          >
            <option value="confidence_asc">{t('review.sortConfidenceAsc')}</option>
            <option value="confidence_desc">{t('review.sortConfidenceDesc')}</option>
            <option value="updated_desc">{t('review.sortUpdated')}</option>
          </select>
        </div>
        <div className={styles.toolbarGroup}>
          <label className={styles.toolbarLabel}>{t('review.domainLabel')}</label>
          <select
            className={styles.select}
            value={domainFilter}
            onChange={(e) => setDomainFilter(e.target.value as Domain | '')}
          >
            <option value="">{t('review.domainAll')}</option>
            {ALL_DOMAINS.map((d) => (
              <option key={d} value={d}>
                {t(`domain.${d}`)}
              </option>
            ))}
          </select>
        </div>
        <Button kind="ghost" onClick={() => void load()}>
          {t('review.refreshButton')}
        </Button>
      </div>

      {isLoading && entries.length === 0 ? (
        <div className={styles.loading}>
          <Spinner /> <span>{t('review.loading')}</span>
        </div>
      ) : visible.length === 0 ? (
        <div className={styles.empty}>
          <strong>{t('review.emptyTitle')}</strong>
          <p>{t('review.emptyBody')}</p>
        </div>
      ) : (
        <div className={styles.queue}>
          {visible.map((entry) => (
            <ExcerptRow key={entry.id} entry={entry} onChange={load} />
          ))}
        </div>
      )}
    </AdminShell>
  );
}

// ─── Per-entry row ──────────────────────────────────────────────────────

function ExcerptRow({ entry, onChange }: { entry: KBEntry; onChange: () => Promise<void> }) {
  const { t, tt } = useI18n();
  const body = entry.body as DocumentExcerptBody;

  const [pending, setPending] = useState<'approve' | 'retag' | 'reject' | 'edit' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Re-tag UI state
  const [showRetag, setShowRetag] = useState(false);
  const [retagDomain, setRetagDomain] = useState<Domain>(entry.domain);

  // Reject UI state
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Edit UI state
  const [showEdit, setShowEdit] = useState(false);
  const [editText, setEditText] = useState(body.text_chunk);

  const isEdited = body.text_chunk !== body.original_text_chunk;

  async function onApprove() {
    setPending('approve');
    setError(null);
    const result = await reviewExcerpt(entry.id, { action: 'approve' });
    if (!result.ok) {
      setError(result.message);
      setPending(null);
      return;
    }
    await onChange();
    setPending(null);
  }

  async function onRetagSubmit() {
    setPending('retag');
    setError(null);
    const result = await reviewExcerpt(entry.id, { action: 'retag', domain: retagDomain });
    if (!result.ok) {
      setError(result.message);
      setPending(null);
      return;
    }
    setShowRetag(false);
    await onChange();
    setPending(null);
  }

  async function onRejectSubmit() {
    setPending('reject');
    setError(null);
    const result = await reviewExcerpt(entry.id, {
      action: 'reject',
      ...(rejectReason.trim() ? { reason: rejectReason.trim() } : {}),
    });
    if (!result.ok) {
      setError(result.message);
      setPending(null);
      return;
    }
    setShowReject(false);
    await onChange();
    setPending(null);
  }

  async function onEditSubmit() {
    setPending('edit');
    setError(null);
    const newBody: DocumentExcerptBody = { ...body, text_chunk: editText };
    const result = await patchKBEntry(entry.id, { body: newBody });
    if (!result.ok) {
      setError(result.message);
      setPending(null);
      return;
    }
    setShowEdit(false);
    await onChange();
    setPending(null);
  }

  const confidence = body.confidence ?? 0;
  const confidenceClass =
    confidence < 0.6 ? styles.confLow : confidence < 0.85 ? styles.confMed : styles.confHigh;

  return (
    <article className={styles.row}>
      <header className={styles.rowHeader}>
        <div className={styles.rowHeaderLeft}>
          <span className={styles.location}>
            {body.chapter} · {body.section}
          </span>
          {isEdited && <span className={styles.editedBadge}>{t('review.editedBadge')}</span>}
        </div>
        <div className={styles.rowHeaderRight}>
          <span className={styles.domainPill}>{t(`domain.${entry.domain}`)}</span>
          <span className={`${styles.confidencePill} ${confidenceClass}`}>
            {tt('review.confidenceTemplate', { pct: Math.round(confidence * 100) })}
          </span>
        </div>
      </header>

      <p className={styles.excerpt}>{body.text_chunk}</p>

      {error && (
        <Alert severity="danger" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div className={styles.rowActions}>
        <Button
          kind="primary"
          onClick={onApprove}
          disabled={pending !== null}
        >
          {pending === 'approve' ? <Spinner /> : t('review.approveButton')}
        </Button>
        <Button
          kind="ghost"
          onClick={() => {
            setShowEdit((s) => !s);
            setShowRetag(false);
            setShowReject(false);
          }}
          disabled={pending !== null}
        >
          {t('review.editButton')}
        </Button>
        <Button
          kind="ghost"
          onClick={() => {
            setShowRetag((s) => !s);
            setShowEdit(false);
            setShowReject(false);
          }}
          disabled={pending !== null}
        >
          {t('review.retagButton')}
        </Button>
        <Button
          kind="ghost"
          onClick={() => {
            setShowReject((s) => !s);
            setShowEdit(false);
            setShowRetag(false);
          }}
          disabled={pending !== null}
        >
          {t('review.rejectButton')}
        </Button>
      </div>

      {showEdit && (
        <div className={styles.inlinePanel}>
          <label className={styles.panelLabel}>{t('review.editLabel')}</label>
          <textarea
            className={styles.textarea}
            rows={8}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
          />
          <p className={styles.cardSub}>{t('review.editHint')}</p>
          <div className={styles.panelActions}>
            <Button kind="ghost" onClick={() => setShowEdit(false)}>
              {t('review.cancelButton')}
            </Button>
            <Button kind="primary" onClick={onEditSubmit} disabled={pending !== null}>
              {pending === 'edit' ? <Spinner /> : t('review.saveButton')}
            </Button>
          </div>
        </div>
      )}

      {showRetag && (
        <div className={styles.inlinePanel}>
          <label className={styles.panelLabel}>{t('review.retagLabel')}</label>
          <select
            className={styles.select}
            value={retagDomain}
            onChange={(e) => setRetagDomain(e.target.value as Domain)}
          >
            {ALL_DOMAINS.map((d) => (
              <option key={d} value={d}>
                {t(`domain.${d}`)}
              </option>
            ))}
          </select>
          <div className={styles.panelActions}>
            <Button kind="ghost" onClick={() => setShowRetag(false)}>
              {t('review.cancelButton')}
            </Button>
            <Button kind="primary" onClick={onRetagSubmit} disabled={pending !== null}>
              {pending === 'retag' ? <Spinner /> : t('review.saveButton')}
            </Button>
          </div>
        </div>
      )}

      {showReject && (
        <div className={styles.inlinePanel}>
          <label className={styles.panelLabel}>{t('review.rejectReasonLabel')}</label>
          <textarea
            className={styles.textarea}
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
          <div className={styles.panelActions}>
            <Button kind="ghost" onClick={() => setShowReject(false)}>
              {t('review.cancelButton')}
            </Button>
            <Button kind="danger" onClick={onRejectSubmit} disabled={pending !== null}>
              {pending === 'reject' ? <Spinner /> : t('review.rejectButton')}
            </Button>
          </div>
        </div>
      )}
    </article>
  );
}
